import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Typography, Alert, Button, Input, Form } from 'antd';
import { 
  LockOutlined,
  WarningOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  ReadOutlined,
  RocketOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import './TVDashboard.css';

const { Title, Text } = Typography;

interface TicketStats {
  total: number;
  new: number;
  open: number;
  pending: number;
  solved: number;
  high: number;
  medium: number;
  low: number;
}

interface Product {
  id: string;
  name: string;
  version: string;
  logoUrl: string;
  updatedAt: string;
}

interface NewsItem {
  id: string;
  title: string;
  content: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

interface AlertTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  priority: string;
  created_at: string;
  business_name: string | null;
  assignee_name?: string | null;
}

interface CategoryCount {
  id: string | null;
  name: string;
  count: number;
}

interface CallMeItem {
  id: string;
  store_id: string;
  business_name: string;
  phone: string | null;
  service_type: string | null;
  notes: string | null;
  created_at: string;
}

interface CenterAlert {
  title: string;
  ticketNumber: string;
  subject: string;
  businessName: string | null;
  priority: string;
}

const formatAlertTime = (dateStr: string) =>
  new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

interface TVDashboardProps {
  embedded?: boolean;
}

const TVDashboard: React.FC<TVDashboardProps> = ({ embedded = false }) => {
  const [password, setPassword] = useState<string>('');
  const [passwordInput, setPasswordInput] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [stats, setStats] = useState<TicketStats>({
    total: 0, new: 0, open: 0, pending: 0, solved: 0,
    high: 0, medium: 0, low: 0
  });
  const [lastStats, setLastStats] = useState<TicketStats>({ total: 0, new: 0, open: 0, pending: 0, solved: 0, high: 0, medium: 0, low: 0 });
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [products, setProducts] = useState<Product[]>([]);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [newsIndex, setNewsIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [timeZones, setTimeZones] = useState<{ [key: string]: string }>({});
  const [callMeCount, setCallMeCount] = useState(0);
  const [callMeItems, setCallMeItems] = useState<CallMeItem[]>([]);
  const [highPriorityTickets, setHighPriorityTickets] = useState<AlertTicket[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<CategoryCount[]>([]);
  const [centerAlert, setCenterAlert] = useState<CenterAlert | null>(null);
  const knownTicketIdsRef = useRef<Set<string>>(new Set());
  const ticketsInitializedRef = useRef(false);
  const centerAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [callMeAlertSettings, setCallMeAlertSettings] = useState<{ enabled: boolean; interval: number; tone: string }>({ enabled: true, interval: 5, tone: 'beep' });
  const [hasUserInteractedForSound, setHasUserInteractedForSound] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  const getRequestHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (embedded) {
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else if (password) {
      headers['X-TV-Dashboard-Password'] = password;
    }
    return headers;
  }, [embedded, password]);

  const isReadyToFetch = embedded ? isAuthorized : (isAuthorized && !!password);

  // Unlock audio for playback (must be called from user gesture - click/tap)
  const unlockAudioContext = () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'closed') {
        audioContextRef.current = null;
      } else {
        audioContextRef.current.resume?.().catch(() => {});
        return;
      }
    }
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioContextRef.current = ctx;
      ctx.resume?.().catch(() => {});
    } catch (e) {
      console.warn('[TVDashboard] Could not create AudioContext:', e);
    }
  };

  // Get base URL - ensure we use the correct protocol
  const getBaseURL = () => {
    // Always use the current origin (which should be HTTPS in production)
    // The IIS web.config will proxy /api/* requests to the backend
    const origin = window.location.origin;
    console.log('[TVDashboard] Using base URL:', origin);
    return origin;
  };

  // Set page title
  useEffect(() => {
    if (embedded) return undefined;
    document.title = 'Vitreous Support - TV Dashboard';
    return () => {
      document.title = 'Vitreous Support - Support Ticket Platform';
      if (centerAlertTimerRef.current) {
        clearTimeout(centerAlertTimerRef.current);
      }
    };
  }, [embedded]);

  // Embedded mode: use main app session (no separate TV password)
  useEffect(() => {
    if (embedded) {
      setIsAuthorized(true);
    }
  }, [embedded]);

  // Load password from localStorage on mount and restore session
  useEffect(() => {
    if (embedded) return;
    const savedPassword = localStorage.getItem('tv_dashboard_password');
    if (savedPassword) {
      console.log('[TVDashboard] Restoring session from localStorage');
      setPassword(savedPassword);
      setPasswordInput(savedPassword);
      // Optimistically set as authorized - will be verified on first API call
      setIsAuthorized(true);
    }
  }, [embedded]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      // Calculate time zones
      const zones: { [key: string]: string } = {};
      zones['EST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['CST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['PST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['IST'] = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['PHT'] = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Manila' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['NZT'] = new Date(now.toLocaleString('en-US', { timeZone: 'Pacific/Chatham' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setTimeZones(zones);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle password submission
  const handlePasswordSubmit = async (values: { password?: string }) => {
    unlockAudioContext(); // Unlock audio while we still have user gesture
    setHasUserInteractedForSound(true);
    // Ant Design Form passes values object, but we're using controlled input
    // So we use passwordInput from state, but also check values.password as fallback
    const passwordToUse = passwordInput.trim() || values.password?.trim() || '';
    
    console.log('[TVDashboard] Password submit triggered');
    console.log('[TVDashboard] passwordInput:', passwordInput);
    console.log('[TVDashboard] values.password:', values.password);
    console.log('[TVDashboard] passwordToUse:', passwordToUse);
    
    if (!passwordToUse) {
      setAuthError('Please enter a password');
      return;
    }

    setIsVerifying(true);
    setAuthError('');

    try {
      const url = `${getBaseURL()}/api/auth/tv-dashboard-login`;
      console.log('[TVDashboard] Attempting login to:', url);
      console.log('[TVDashboard] Sending password (length):', passwordToUse.length);
      
      const response = await axios.post(url, {
        password: passwordToUse
      });
      
      console.log('[TVDashboard] Login response:', response.data);
      
      if (response.data.success) {
        console.log('[TVDashboard] Login successful, storing password');
        localStorage.setItem('tv_dashboard_password', passwordToUse);
        setPassword(passwordToUse);
        setIsAuthorized(true);
        setAuthError('');
      } else {
        console.log('[TVDashboard] Login failed - success flag is false');
        setAuthError('Invalid password. Please check and try again.');
        setIsAuthorized(false);
      }
    } catch (error: any) {
      console.error('[TVDashboard] Password verification failed:', error);
      console.error('[TVDashboard] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      if (error.response?.status === 401) {
        setAuthError(error.response?.data?.error || 'Invalid password. Please check and try again.');
      } else if (error.response?.status === 404) {
        setAuthError('Login endpoint not found. Please contact administrator.');
      } else {
        setAuthError(error.response?.data?.error || 'Failed to connect to server. Please check your connection.');
      }
      setIsAuthorized(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // Verify password in background (non-blocking) - only if we have a password but aren't authorized
  useEffect(() => {
    const verifyPassword = async () => {
      // Only verify if we have a password but aren't authorized yet
      // This handles the case where password was just set
      if (!password || isAuthorized) return;

      console.log('[TVDashboard] Verifying password in background...');
      try {
        const response = await axios.post('/api/auth/tv-dashboard-login', {
          password: password
        });
        
        if (response.data.success) {
          console.log('[TVDashboard] Background verification successful');
          setIsAuthorized(true);
          setAuthError('');
        } else {
          console.log('[TVDashboard] Background verification failed - success flag false');
          // Don't clear everything, just mark as unauthorized
          setIsAuthorized(false);
        }
      } catch (error: any) {
        console.error('[TVDashboard] Background password verification failed:', error);
        // Don't clear on background verification failure - let the first API call handle it
        setIsAuthorized(false);
      }
    };

    verifyPassword();
  }, [password, isAuthorized]);

  // Handle logout/clear password
  const handleClearPassword = () => {
    localStorage.removeItem('tv_dashboard_password');
    setPassword('');
    setPasswordInput('');
    setIsAuthorized(false);
    setAuthError('');
    ticketsInitializedRef.current = false;
    knownTicketIdsRef.current = new Set();
    setHighPriorityTickets([]);
    setCallMeItems([]);
    setCenterAlert(null);
  };

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => {
        console.log('Audio play failed, using beep:', e);
        // Fallback: create beep using Web Audio API
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      });
    }
  }, []);

  const showCenterAlert = useCallback((ticket: AlertTicket) => {
    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
    }
    setCenterAlert({
      title: 'New Ticket',
      ticketNumber: ticket.ticket_number,
      subject: ticket.subject,
      businessName: ticket.business_name,
      priority: ticket.priority
    });
    playNotificationSound();
    centerAlertTimerRef.current = setTimeout(() => {
      setCenterAlert(null);
      centerAlertTimerRef.current = null;
    }, 5000);
  }, [playNotificationSound]);

  const processTvAlerts = useCallback((data: {
    highPriorityTickets?: AlertTicket[];
    callMeItems?: CallMeItem[];
    callMeCount?: number;
    activeTickets?: AlertTicket[];
    categoryCounts?: CategoryCount[];
  }) => {
    const highTickets = data.highPriorityTickets || [];
    const callMe = data.callMeItems || [];
    const activeTickets = data.activeTickets || [];

    setHighPriorityTickets(highTickets);
    setCallMeItems(callMe);
    setCallMeCount(Number(data.callMeCount ?? callMe.length));
    setCategoryCounts(data.categoryCounts || []);

    const currentIds = new Set(activeTickets.map((t) => t.id));
    if (!ticketsInitializedRef.current) {
      knownTicketIdsRef.current = currentIds;
      ticketsInitializedRef.current = true;
      return;
    }

    const newTickets = activeTickets.filter((t) => !knownTicketIdsRef.current.has(t.id));
    if (newTickets.length > 0) {
      const newest = [...newTickets].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      showCenterAlert(newest);
    }

    knownTicketIdsRef.current = currentIds;
  }, [showCenterAlert]);

  const fetchTvAlerts = useCallback(async () => {
    if (!isReadyToFetch) return;
    try {
      const res = await fetch(`${getBaseURL()}/api/dashboard/tv-alerts`, {
        method: 'GET',
        headers: getRequestHeaders(),
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!res.ok) return;
      const data = await res.json();
      processTvAlerts(data);
    } catch (e) {
      console.warn('[TVDashboard] TV alerts fetch failed:', e);
    }
  }, [isReadyToFetch, getRequestHeaders, processTvAlerts]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!isReadyToFetch) return;

    try {
      console.log('[TVDashboard] Fetching dashboard data...');
      const baseURL = getBaseURL();
      console.log('[TVDashboard] Base URL:', baseURL);
      
      // Use fetch directly to avoid axios interceptor issues
      const statsUrl = `${baseURL}/api/dashboard/stats`;
      console.log('[TVDashboard] Fetching stats from:', statsUrl);
      
      const statsResponse = await fetch(statsUrl, {
        method: 'GET',
        headers: getRequestHeaders(),
        credentials: 'same-origin'
      });
      
      console.log('[TVDashboard] Stats response status:', statsResponse.status);
      
      if (!statsResponse.ok) {
        throw new Error(`Stats request failed: ${statsResponse.status} ${statsResponse.statusText}`);
      }
      
      const statsData = await statsResponse.json();
      console.log('[TVDashboard] Stats data:', statsData);
      const newStats = statsData || {};

      const updatedStats = {
        total: Number(newStats.total || 0),
        new: Number(newStats.new || 0),
        open: Number(newStats.open || 0),
        pending: Number(newStats.pending || 0),
        solved: Number(newStats.solved || 0),
        high: Number(newStats.high || 0),
        medium: Number(newStats.medium || 0),
        low: Number(newStats.low || 0)
      };

      setLastStats(updatedStats);
      setStats(updatedStats);
      setLastUpdateTime(new Date());

      await fetchTvAlerts();

      console.log('[TVDashboard] Data fetch completed successfully');
    } catch (error: any) {
      console.error('[TVDashboard] Failed to fetch dashboard data:', error);
      console.error('[TVDashboard] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      if (error.response?.status === 401 && !embedded) {
        // Only clear and show error if we're actually unauthorized
        // This handles the case where password was changed or is invalid
        console.log('[TVDashboard] 401 Unauthorized - password may be invalid');
        setIsAuthorized(false);
        setAuthError('Password invalid or expired. Please enter the correct TV Dashboard password.');
        // Clear invalid password from localStorage
        localStorage.removeItem('tv_dashboard_password');
        setPassword('');
        setPasswordInput('');
      }
    }
  }, [isReadyToFetch, embedded, getRequestHeaders, fetchTvAlerts]);

  // Fast TV alerts polling for near-realtime queue/popup updates
  const fetchCallMeCountFast = useCallback(async () => {
    await fetchTvAlerts();
  }, [fetchTvAlerts]);

  // Fetch Call Me alert settings
  useEffect(() => {
    if (!isReadyToFetch) return;

    const fetchCallMeAlertSettings = async () => {
      try {
        const res = await fetch(`${getBaseURL()}/api/settings/tv-dashboard-call-me-alert`, {
          headers: getRequestHeaders(),
          credentials: 'same-origin'
        });
        if (res.ok) {
          const data = await res.json();
          const tones = ['beep', 'chime', 'alert', 'soft', 'double'];
          const tone = data.tone && tones.includes(data.tone) ? data.tone : 'beep';
          setCallMeAlertSettings({
            enabled: data.enabled !== false,
            interval: Math.max(5, Math.min(60, data.interval || 5)),
            tone
          });
        }
      } catch (e) {
        console.warn('[TVDashboard] Could not fetch Call Me alert settings:', e);
      }
    };
    fetchCallMeAlertSettings();

    const alertSettingsInterval = setInterval(fetchCallMeAlertSettings, 60000);

    return () => {
      clearInterval(alertSettingsInterval);
    };
  }, [isReadyToFetch, getRequestHeaders]);

  // Fetch product release info and news feed
  useEffect(() => {
    if (!isReadyToFetch) return;

    const fetchProductAndNews = async () => {
      const baseURL = getBaseURL();
      const headers = getRequestHeaders();

      try {
        const [productRes, newsRes] = await Promise.all([
          fetch(`${baseURL}/api/settings/tv-dashboard-products?_cb=${Date.now()}`, {
            headers,
            credentials: 'same-origin'
          }),
          fetch(`${baseURL}/api/settings/tv-dashboard-news?_cb=${Date.now()}`, {
            headers,
            credentials: 'same-origin'
          })
        ]);

        if (productRes.ok) {
          const data = await productRes.json();
          setProducts(data.products || []);
        }
        if (newsRes.ok) {
          const data = await newsRes.json();
          setNewsItems(data.news || []);
        }
      } catch (error) {
        console.warn('[TVDashboard] Could not fetch product/news:', error);
      }
    };

    fetchProductAndNews();
    const interval = setInterval(fetchProductAndNews, 60 * 1000);
    return () => clearInterval(interval);
  }, [isReadyToFetch, getRequestHeaders]);

  // Rotate news feed items
  useEffect(() => {
    if (newsItems.length <= 1) return;
    const interval = setInterval(() => {
      setNewsIndex((prev) => (prev + 1) % newsItems.length);
    }, 15000);
    return () => clearInterval(interval);
  }, [newsItems.length]);

  useEffect(() => {
    if (newsIndex >= newsItems.length) {
      setNewsIndex(0);
    }
  }, [newsItems.length, newsIndex]);

  // Create AudioContext when dashboard is shown (starts suspended; resume on user click)
  useEffect(() => {
    if (!isAuthorized || audioContextRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        audioContextRef.current = new Ctx();
      }
    } catch (e) {
      console.warn('[TVDashboard] Could not create AudioContext:', e);
    }
  }, [isAuthorized]);

  // Play a tone using Web Audio API
  const playTone = (toneId: string, ctx: AudioContext) => {
    const t = ctx.currentTime;
    const playNote = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, start);
      gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    };
    switch (toneId) {
      case 'chime':
        playNote(523.25, t, 0.2);
        playNote(659.25, t + 0.15, 0.25);
        break;
      case 'alert':
        playNote(1200, t, 0.4);
        break;
      case 'soft':
        playNote(440, t, 0.35);
        break;
      case 'double':
        playNote(880, t, 0.15);
        playNote(880, t + 0.25, 0.15);
        break;
      default: // beep
        playNote(880, t, 0.3);
    }
  };

  // Call Me alert: play sound every N seconds when queue has items
  // Uses Web Audio API with pre-unlocked context (resumed on user click)
  useEffect(() => {
    if (!callMeAlertSettings.enabled || callMeCount === 0) return;
    const tone = callMeAlertSettings.tone || 'beep';
    const playCallMeAlert = () => {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      if (ctx.state === 'closed') {
        audioContextRef.current = null;
        return;
      }
      (async () => {
        try {
          if (ctx.state === 'suspended') await ctx.resume();
          playTone(tone, ctx);
        } catch (e) {
          console.warn('[TVDashboard] Call Me alert failed:', e);
        }
      })();
    };
    playCallMeAlert();
    const id = setInterval(playCallMeAlert, callMeAlertSettings.interval * 1000);
    return () => clearInterval(id);
  }, [callMeCount, callMeAlertSettings.enabled, callMeAlertSettings.interval, callMeAlertSettings.tone]);

  // Fetch data when authorized
  useEffect(() => {
    if (!isReadyToFetch) {
      console.log('[TVDashboard] Not fetching data - isAuthorized:', isAuthorized, 'embedded:', embedded);
      return;
    }

    console.log('[TVDashboard] Starting data fetch - isAuthorized:', isAuthorized, 'embedded:', embedded);
    fetchDashboardData();
    const interval = setInterval(() => {
      console.log('[TVDashboard] Auto-refreshing data...');
      fetchDashboardData();
    }, 60000); // Refresh every 60 seconds (reduces load / 503 risk)

    return () => clearInterval(interval);
  }, [isReadyToFetch, embedded, isAuthorized, fetchDashboardData]);

  // Keep Call Me queue count highly responsive (independent of full dashboard refresh)
  useEffect(() => {
    if (!isReadyToFetch) return;

    fetchCallMeCountFast();
    const pollMs = callMeCount > 0 ? 2000 : 5000;
    const callMeInterval = setInterval(fetchCallMeCountFast, pollMs);
    return () => clearInterval(callMeInterval);
  }, [isReadyToFetch, callMeCount, fetchCallMeCountFast]);

  const currentNewsItem = newsItems.length > 0 ? newsItems[newsIndex % newsItems.length] : null;

  const hasProductInfo = products.length > 0;

  // Logo URL with cache busting - memoized to prevent infinite re-renders
  const logoUrl = useMemo(() => {
    const basePath = process.env.PUBLIC_URL || '';
    const cacheBuster = `${Date.now()}_${Math.random()}`;
    return `${basePath}/logo.png?v=${cacheBuster}`.replace('//', '/');
  }, []); // Empty dependency array - only calculate once on mount

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Auth screen
  if (!isAuthorized) {
    if (embedded) {
      return (
        <Alert
          type="warning"
          showIcon
          message="TV Dashboard unavailable"
          description="Sign in to view live support statistics."
          style={{ marginBottom: 24 }}
        />
      );
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
            `,
            opacity: 0.6,
          }}
        />

        {/* Centered Login Card */}
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: '56px 48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 1,
            animation: 'fadeInUp 0.6s ease-out',
          }}
        >
          {/* Logo/Branding at top */}
          <div style={{ marginBottom: 48, textAlign: 'center' }}>
            {logoUrl ? (
              <img
                key={logoUrl}
                src={logoUrl}
                alt="Vitreous Support Logo"
                style={{
                  maxWidth: '320px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '0 auto 32px',
                  transition: 'opacity 0.3s ease',
                }}
                onError={(e) => {
                  console.error('[TVDashboard] Failed to load logo from:', logoUrl);
                }}
                onLoad={() => {
                  console.log('[TVDashboard] Logo loaded successfully from:', logoUrl);
                }}
              />
            ) : (
              <Title level={1} style={{ fontSize: 36, fontWeight: 700, margin: '0 0 32px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                Vitreous Support
              </Title>
            )}
            <Title
              level={2}
              style={{
                fontSize: 28,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a',
                lineHeight: 1.3,
              }}
            >
              TV Dashboard Access
            </Title>
            <Text type="secondary" style={{ fontSize: 15 }}>
              Enter your password to view the live operations dashboard
            </Text>
          </div>

          {/* Error Alert */}
          {authError && (
            <Alert
              message={authError}
              type="error"
              showIcon
              closable
              onClose={() => setAuthError('')}
              style={{
                marginBottom: 28,
                borderRadius: 12,
                border: '1px solid #ffccc7',
                backgroundColor: '#fff2f0',
              }}
            />
          )}

          {/* Login Form */}
          <Form
            onFinish={handlePasswordSubmit}
            layout="vertical"
            size="large"
            style={{ width: '100%' }}
          >
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter a password' }]}
              style={{ marginBottom: 32 }}
            >
              <Input.Password
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                prefix={<LockOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
                placeholder="TV Dashboard Password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                disabled={isVerifying}
                autoComplete="current-password"
                style={{
                  height: 52,
                  borderRadius: 12,
                  fontSize: 15,
                  border: '1.5px solid #e8e8e8',
                  transition: 'all 0.2s ease',
                  paddingLeft: 44,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.05)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e8e8e8';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isVerifying}
                block
                style={{
                  height: 52,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.3)';
                }}
              >
                {isVerifying ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </Form.Item>
          </Form>

          {/* Help Text */}
          <Alert
            message="How to get the password:"
            description={
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>Go to Settings → TV Dashboard</li>
                <li>Set a password for TV Dashboard access</li>
                <li>Enter that password here to view the dashboard</li>
              </ul>
            }
            type="info"
            showIcon
            style={{
              borderRadius: 12,
              marginTop: 24,
            }}
          />
        </div>
      </div>
    );
  }

  const handleDashboardClick = () => {
    unlockAudioContext();
    setHasUserInteractedForSound(true);
  };

  // Main dashboard
  return (
    <div className={`tv-dashboard${embedded ? ' embedded' : ''}`} onClick={handleDashboardClick} role="presentation">
      <audio ref={audioRef} preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>

      {/* Tap to enable sound - shown when Call Me queue has items but user hasn't interacted yet */}
      {callMeCount > 0 && callMeAlertSettings.enabled && !hasUserInteractedForSound && (
        <Alert
          message="Tap anywhere to enable Call Me alert sound"
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ margin: 12, borderRadius: 8 }}
        />
      )}

      {/* Top Header Bar */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="header-logo-container">
            <img
              src={logoUrl}
              alt="Vitreous Support Logo"
              className="header-logo-image"
              onError={(e) => {
                console.error('[TVDashboard] Failed to load logo from:', logoUrl);
                // Fallback to text if logo fails
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const container = target.parentElement;
                if (container) {
                  container.innerHTML = '<div class="header-logo">Support Ticket Statistics</div>';
                }
              }}
              onLoad={() => {
                console.log('[TVDashboard] Logo loaded successfully from:', logoUrl);
              }}
            />
          </div>
        </div>
        <div className="header-center">
        </div>
        <div className="header-right">
          <div className="time-zones">
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">EST</div>
              <div className="timezone-time">{timeZones['EST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">CST</div>
              <div className="timezone-time">{timeZones['CST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">PST</div>
              <div className="timezone-time">{timeZones['PST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/in.png" 
                alt="IN" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'IN';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">IST</div>
              <div className="timezone-time">{timeZones['IST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/ph.png" 
                alt="PH" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'PH';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">PHT</div>
              <div className="timezone-time">{timeZones['PHT'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src={`${process.env.PUBLIC_URL || ''}/flags/nz.png`}
                alt="NZ" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.src = 'https://flagcdn.com/w40/nz.png';
                  }
                }}
              />
              <div className="timezone-label">NZT</div>
              <div className="timezone-time">{timeZones['NZT'] || '--:--:--'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Main KPI Cards Row */}
        <div className="kpi-row">
            {/* Total Active */}
            <div className={`kpi-card total-active ${stats.total > lastStats.total ? 'pulse' : ''}`}>
              <div className="kpi-value">{stats.open + stats.pending + stats.high}</div>
              <div className="kpi-label">Total Active</div>
            </div>

          {/* Call Me */}
          <div className={`kpi-card call-me ${callMeCount > 0 ? 'glow' : ''}`}>
            <div className="kpi-value">{callMeCount}</div>
            <div className="kpi-label">Call Me</div>
            {callMeCount > 0 && <div className="kpi-subtext">Waiting</div>}
          </div>

          {/* Open Tickets */}
          <div className={`kpi-card open ${stats.open > lastStats.open ? 'flash' : ''}`}>
            <div className="kpi-value">{stats.open}</div>
            <div className="kpi-label">Open</div>
            {stats.open > lastStats.open && (
              <div className="kpi-subtext">+{stats.open - lastStats.open} new</div>
            )}
          </div>

          {/* Pending */}
          <div className={`kpi-card pending ${stats.pending > lastStats.pending ? 'pulse' : ''}`}>
            <div className="kpi-value">{stats.pending}</div>
            <div className="kpi-label">Pending</div>

          </div>

          {/* High Priority */}
          <div className={`kpi-card high-priority ${stats.high > 0 ? 'glow' : ''}`}>
            <div className="kpi-value">{stats.high}</div>
            <div className="kpi-label">High Priority</div>
            {stats.high > 0 && <div className="kpi-subtext">Requires attention</div>}
          </div>

          {/* Solved Today */}
          <div className={`kpi-card solved ${stats.solved > lastStats.solved ? 'checkmark' : ''}`}>
            <div className="kpi-value">{stats.solved}</div>
            <div className="kpi-label">Solved Today</div>
            {stats.solved > lastStats.solved && (
              <div className="kpi-subtext">+{stats.solved - lastStats.solved} resolved</div>
            )}
          </div>
        </div>

        {categoryCounts.length > 0 && (
          <div className="kpi-row kpi-row-categories">
            {categoryCounts.map((cat) => (
              <div className="kpi-card category" key={cat.id || 'unassigned'} title={cat.name}>
                <div className="kpi-label">{cat.name.replace(/\s+/g, ' ').trim()}</div>
                <div className="kpi-value">{cat.count}</div>
              </div>
            ))}
          </div>
        )}

        <div className="dashboard-main-sections">
          {(highPriorityTickets.length > 0 || callMeItems.length > 0) && (
            <div className="tv-alert-sections">
              {highPriorityTickets.length > 0 && (
                <div className="section-card tv-alert-card high-priority-alert-card">
                  <div className="section-card-header">
                    <h2 className="section-card-title">
                      <ExclamationCircleOutlined /> High Priority Tickets
                    </h2>
                    <span className="tv-alert-count">{highPriorityTickets.length}</span>
                  </div>
                  <div className="tv-alert-list">
                    {highPriorityTickets.map((ticket) => (
                      <div className="tv-alert-row" key={ticket.id}>
                        <div className="tv-alert-row-main">
                          <span className="tv-alert-ticket-number">#{ticket.ticket_number}</span>
                          <span className="tv-alert-subject">{ticket.subject}</span>
                        </div>
                        <div className="tv-alert-row-meta">
                          {ticket.business_name && <span>{ticket.business_name}</span>}
                          {ticket.assignee_name && <span>Agent: {ticket.assignee_name}</span>}
                          <span className="tv-alert-status">{ticket.status}</span>
                          <span>{formatAlertTime(ticket.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {callMeItems.length > 0 && (
                <div className="section-card tv-alert-card call-me-alert-card">
                  <div className="section-card-header">
                    <h2 className="section-card-title">
                      <PhoneOutlined /> Call Me Queue
                    </h2>
                    <span className="tv-alert-count">{callMeItems.length}</span>
                  </div>
                  <div className="tv-alert-list">
                    {callMeItems.map((item) => (
                      <div className="tv-alert-row" key={item.id}>
                        <div className="tv-alert-row-main">
                          <span className="tv-alert-ticket-number">{item.store_id || '—'}</span>
                          <span className="tv-alert-subject">{item.business_name || 'Unknown business'}</span>
                        </div>
                        <div className="tv-alert-row-meta">
                          {item.service_type && <span>{item.service_type}</span>}
                          {item.phone && <span>{item.phone}</span>}
                          {item.notes && <span className="tv-alert-notes">{item.notes}</span>}
                          <span>{formatAlertTime(item.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {currentNewsItem && (
            <div className="news-feed-section">
              <div className="section-card news-feed-card">
                <div className="section-card-header">
                  <h2 className="section-card-title">
                    <ReadOutlined /> News Feed
                  </h2>
                  {newsItems.length > 1 && (
                    <span className="news-feed-indicator">
                      {newsIndex + 1} / {newsItems.length}
                    </span>
                  )}
                </div>
                <div className="news-feed-body" key={currentNewsItem.id}>
                  <div className="news-feed-title">{currentNewsItem.title}</div>
                  <div className="news-feed-content">{currentNewsItem.content}</div>
                </div>
              </div>
            </div>
          )}

          {hasProductInfo && (
            <div className="product-release-section">
              <div className="section-card product-release-card">
                <div className="section-card-header">
                  <h2 className="section-card-title">
                    <RocketOutlined /> Product Release
                  </h2>
                </div>
                <div className="product-release-body">
                  {products.map((product) => (
                    <div className="product-release-item" key={product.id}>
                      <div className="product-release-logo-box">
                        {product.logoUrl ? (
                          <img
                            src={`${getBaseURL()}${product.logoUrl}?v=${encodeURIComponent(product.updatedAt || product.version || 'logo')}`}
                            alt={product.name}
                            className="product-release-logo"
                          />
                        ) : (
                          <div className="product-release-name">{product.name}</div>
                        )}
                      </div>
                      {product.version && (
                        <div className="product-release-version">v{product.version.replace(/^v/i, '')}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Last Updated */}
        <div className="last-updated" style={{ textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
          Last updated {Math.floor((new Date().getTime() - lastUpdateTime.getTime()) / 1000)} seconds ago
        </div>

        {/* Logout Button */}
        {!embedded && (
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleClearPassword}
              style={{ color: '#fff', fontSize: '18px', fontWeight: 600, padding: '8px 24px' }}
            >
              Logout
            </Button>
          </div>
        )}
      </div>

      {centerAlert && (
        <div className="tv-center-alert-overlay" role="alert" aria-live="assertive">
          <div className="tv-center-alert-modal">
            <div className="tv-center-alert-icon">
              <ExclamationCircleOutlined />
            </div>
            <div className="tv-center-alert-title">{centerAlert.title}</div>
            <div className="tv-center-alert-ticket">#{centerAlert.ticketNumber}</div>
            <div className="tv-center-alert-subject">{centerAlert.subject}</div>
            {centerAlert.businessName && (
              <div className="tv-center-alert-business">{centerAlert.businessName}</div>
            )}
            <div className="tv-center-alert-priority">
              Priority: {centerAlert.priority}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TVDashboard;
